/* ContextualMap · Founder Operating System · Batch S1 · Aug 2026
 * ==================================================================
 * The Founder Operating System's first pair of eyes.
 *
 * Reusable spatial surface mounted inside existing OS surfaces only
 * (Today + Work Mode). Consumes the canonical `/api/spatial/context`
 * envelope — every marker is tagged with its source and status,
 * approximate positions are visually distinct from confirmed ones,
 * and honest empty-state wording replaces the map when no location
 * data is available.
 *
 * Native lesson guidance and its map are provided by Google Navigation.
 * Browser map previews use OpenStreetMap tiles; routes come from Google Routes.
 *
 * Device position is TRANSIENT. Today uses a one-shot location check.
 * Work Mode starts continuous `watchPosition` only after the instructor taps
 * Start navigation. The trace remains in memory; only bounded snapshots tied
 * to mapped challenge encounters, observations, or Repeat may become evidence.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, Popup, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Navigation, Compass, Info, Clock, AlertCircle, Layers, X, Plus, Target, Volume2, VolumeX, RotateCcw, Mic } from "lucide-react";
import { toast } from "sonner";
import useLiveGoogleNavigation, { EMPTY_NAVIGATION_ITEMS, formatNavigationDistance, parseRouteGeometry } from "@/hooks/useLiveGoogleNavigation";
import { getNativeGoogleNavigationPlugin } from "@/native/googleNavigationBridge";
import { mountNavigationMapSurface } from "@/native/navigationMapSurface";
import { selectMapViewportPoints } from "@/utils/mapViewport";
import { requestFreshRouteLocation } from "@/lib/activeRouteRecovery";
import { normalizeDestinationVoiceTranscript } from "./work_mode/destinationVoiceSearch.mjs";
import { runDestinationCommand, createDestinationConfirmation, destinationSearchProvider, isTestCentreQuery } from "./work_mode/lessonDestinationCommand.mjs";

function NativeNavigationSurface({ lessonEventId }) {
  const ref = useRef(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setError(null);
    return mountNavigationMapSurface({ element: ref.current,
      plugin: getNativeGoogleNavigationPlugin(), lessonEventId, onError: setError });
  }, [lessonEventId]);
  return <div ref={ref} data-testid="native-google-navigation-surface"
    className="h-full w-full bg-transparent" aria-label="Navigation map">
    {error && <p role="alert" data-ndg-map-overlay className="bg-slate-950 text-amber-200 p-4">{error}</p>}
  </div>;
}

const API = process.env.REACT_APP_BACKEND_URL || "";

// UK-centred default — never used as a pretend "current position".
const DEFAULT_CENTRE = [54.5, -3.5];
const DEFAULT_ZOOM   = 5;

const OSM_TILE_URL     = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION  = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';


// Batch S2 · Educational Environment Overlay
// Level colour palette. Blueprint direction: L1 green → L4 red.
const LEVEL_COLOUR = {
  1: { stroke: "#166534", fill: "#22c55e" },  // Green
  2: { stroke: "#854d0e", fill: "#eab308" },  // Yellow
  3: { stroke: "#7c2d12", fill: "#f97316" },  // Orange
  4: { stroke: "#7f1d1d", fill: "#ef4444" },  // Red
};
const LEVEL_COLOUR_UNKNOWN = { stroke: "#334155", fill: "#94a3b8" };

const CATEGORY_KEYS = [
  "junction", "roundabout", "hill_start", "reverse_practice",
  "parallel_parking", "independent_driving", "general_practice",
  "traffic_lights", "dual_carriageway", "rural_roads",
];

const STATUS_LABEL = {
  confirmed:              "Confirmed",
  approximate:            "Approximate",
  missing:                "Not set",
  unresolved:             "Address unresolved",
  permission_denied:      "Location permission needed",
  geocoding_unavailable:  "Geocoding unavailable",
};

const STATUS_TONE = {
  confirmed:              "text-emerald-700 bg-emerald-50 border-emerald-200",
  approximate:            "text-amber-700   bg-amber-50   border-amber-200",
  missing:                "text-slate-500   bg-slate-50   border-slate-200",
  unresolved:             "text-slate-500   bg-slate-50   border-slate-200",
  permission_denied:      "text-slate-500   bg-slate-50   border-slate-200",
  geocoding_unavailable:  "text-slate-500   bg-slate-50   border-slate-200",
};

function StatusPill({ status, children, testid }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.missing;
  return (
    <span
      data-testid={testid}
      data-status={status}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`}
    >
      {children ?? STATUS_LABEL[status] ?? "Unknown"}
    </span>
  );
}

function FitToContext({ surface, driving, pickup, dropoff, device, routePoints = [], readinessRoutePoints = [] }) {
  const map = useMap();
  useEffect(() => {
    const pts = selectMapViewportPoints({
      surface, driving, pickup, dropoff, device, routePoints, readinessRoutePoints,
    });
    if (pts.length === 1) {
      map.setView(pts[0], 13, { animate: true });
    } else if (pts.length > 1) {
      map.fitBounds(pts, { padding: [40, 40], maxZoom: 14, animate: true });
    }
  }, [map, surface, driving, pickup, dropoff, device, routePoints, readinessRoutePoints]);
  return null;
}

function FollowLiveDevice({ device, active }) {
  const map = useMap();
  useEffect(() => {
    if (!active || device?.latitude == null || device?.longitude == null) return;
    map.setView(
      [Number(device.latitude), Number(device.longitude)],
      17,
      { animate: true },
    );
  }, [map, active, device?.latitude, device?.longitude]);
  return null;
}

// Batch S3 — captures a single tap while capture mode is active
// and hands the coordinates to the parent. Docked, not floating.
function CaptureTapBridge({ enabled, onPick }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onPick?.({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
}

// Batch S5 — captures a single tap while move mode is active
// and hands the corrected coordinates to the parent. Docked, not floating.
function MoveTapBridge({ enabled, onPick }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onPick?.({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
}

// Batch S2 — reports the visible map bounds to the parent so the
// environment overlay can refetch when the instructor pans/zooms.
function BoundsBridge({ onBounds }) {
  const map = useMap();
  const lastBoundsKey = useRef(null);
  useEffect(() => {
    const emit = () => {
      const b = map.getBounds();
      const bounds = {
        min_lat: b.getSouth(),
        min_lng: b.getWest(),
        max_lat: b.getNorth(),
        max_lng: b.getEast(),
      };
      // Leaflet can emit repeated moveend events after fitBounds animations
      // and marker redraws. Do not refetch an unchanged viewport forever.
      const key = [
        bounds.min_lat, bounds.min_lng, bounds.max_lat, bounds.max_lng,
      ].map((value) => Number(value).toFixed(5)).join(":");
      if (lastBoundsKey.current === key) return;
      lastBoundsKey.current = key;
      onBounds?.(bounds);
    };
    emit();
    map.on("moveend", emit);
    return () => { map.off("moveend", emit); };
  }, [map, onBounds]);
  return null;
}

function fmtTime(iso) {
  if (!iso) return "—";
  const localClock = String(iso).match(/T(\d{2}):(\d{2})/);
  if (localClock) return `${localClock[1]}:${localClock[2]}`;
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function fmtMinutes(m) {
  if (m == null) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

export function ContextualMap({
  surface = "today",
  className = "",
  onNeedsSetup = null,
  lessonEventId = null,
  routeMode = "lesson",
  routeGeometry = null,
  navigationInstructions = EMPTY_NAVIGATION_ITEMS,
  navigationWaypoints = EMPTY_NAVIGATION_ITEMS,
  routeProvider = null,
  preferredTeachingLevel = null,
  teachingChallenges = EMPTY_NAVIGATION_ITEMS,
  allowManualCapture = true,
  onEnvironmentSaved = null,
  onNavigationContextChange = null,
  onNavigationControlChange = null,
  onChallengePassed = null,
  onChallengeRepeated = null,
  onRepeatActionChange = null,
  onDestinationActionChange = null,
  autoStartNavigation = false,
  driving = false,
  routePending = false,
  routeMessage = null,
  routeStatusOwnedByParent = false,
  controlsVisible = true,
  onSearchModeChange = null,
  onJourneyCheckpoint = null,
  part3SessionId = null,
  part3Phase = null,
  part3PhaseVersion = null,
  onPart3Route = null,
}) {
  const isPart3Journey = routeMode === "part3";
  const isPickupJourney = surface === "work_mode" && routeMode === "pickup";
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stopSearch, setStopSearch] = useState("");
  const [stopPlaces, setStopPlaces] = useState([]);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopSearchBusy, setStopSearchBusy] = useState(false);
  const [stopPreview, setStopPreview] = useState(null);
  const [stopPreviewBusy, setStopPreviewBusy] = useState(false);
  const [stopActionError, setStopActionError] = useState(null);
  const [searchModeOpen, setSearchModeOpen] = useState(false);
  const [stopVoiceListening, setStopVoiceListening] = useState(false);
  const stopVoiceRecognitionRef = useRef(null);
  const destinationVoiceScopeRef = useRef(null);
  const destinationVoiceHandlerRef = useRef(null);
  const destinationVoiceBusyRef = useRef(false);
  const destinationCommitInFlightRef = useRef(false);
  const destinationVoiceRequestRef = useRef(0);
  const destinationConfirmationRef = useRef(createDestinationConfirmation());
  const destinationSearchScopeRef = useRef(null);
  const onSearchModeChangeRef = useRef(onSearchModeChange);
  onSearchModeChangeRef.current = onSearchModeChange;
  const [device, setDevice] = useState({ status: "idle", latitude: null, longitude: null, permission: null, requestedAt: null });
  const [readiness, setReadiness] = useState(null);
  const [readinessError, setReadinessError] = useState(null);
  const onNavigationContextChangeRef = useRef(onNavigationContextChange);
  const onNavigationControlChangeRef = useRef(onNavigationControlChange);
  useLayoutEffect(() => {
    // The teaching map may be reused for another lesson. Revoke both manual
    // and spoken destination work before registering the new lesson controls.
    const scope = {};
    destinationSearchScopeRef.current = scope;
    destinationVoiceRequestRef.current++;
    destinationVoiceBusyRef.current = false;
    destinationCommitInFlightRef.current = false;
    destinationConfirmationRef.current.cancel();
    setStopSearch("");
    setStopPlaces([]);
    setSelectedStop(null);
    setStopPreview(null);
    setStopSearchBusy(false);
    setStopPreviewBusy(false);
    setStopActionError(null);
    setSearchModeOpen(false);
    setStopVoiceListening(false);
    onSearchModeChangeRef.current?.(false);
    return () => {
      if (destinationSearchScopeRef.current === scope) destinationSearchScopeRef.current = null;
      destinationVoiceScopeRef.current = null;
      destinationVoiceRequestRef.current++;
      destinationConfirmationRef.current.cancel();
      const recognition = stopVoiceRecognitionRef.current;
      stopVoiceRecognitionRef.current = null;
      if (recognition) {
        recognition.onresult = null;
        recognition.onend = null;
        recognition.onerror = null;
        try { recognition.abort(); } catch {}
      }
    };
  }, [surface, routeMode, lessonEventId, driving]);
  useEffect(() => () => {
    const recognition = stopVoiceRecognitionRef.current;
    stopVoiceRecognitionRef.current = null;
    if (recognition) {
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      try { recognition.abort(); } catch {}
    }
  }, []);
  const mapTileUrl = OSM_TILE_URL;
  const mapAttribution = OSM_ATTRIBUTION;

  const liveNavigation = useLiveGoogleNavigation({
    lessonEventId,
    routeMode,
    part3SessionId,
    part3Phase,
    part3PhaseVersion,
    onPart3Route,
    initialRouteGeometry: routeGeometry,
    initialInstructions: navigationInstructions,
    initialWaypoints: navigationWaypoints,
    teachingChallenges,
    onChallengePassed: (event) => onChallengePassed?.({
      ...event,
      routeProvider: routeProvider || "google_routes",
    }),
    onChallengeRepeated: (event) => onChallengeRepeated?.({
      ...event,
      routeProvider: routeProvider || "google_routes",
    }),
    autoStart: surface === "work_mode" && autoStartNavigation,
  });

  useEffect(() => {
    onNavigationContextChangeRef.current = onNavigationContextChange;
  }, [onNavigationContextChange]);

  useEffect(() => {
    onNavigationControlChangeRef.current = onNavigationControlChange;
  }, [onNavigationControlChange]);

  // Expose only the current lesson-scoped Repeat action to the floating
  // Work Mode controls; revoke it as soon as navigation pauses or unmounts.
  useEffect(() => {
    if (typeof onRepeatActionChange !== "function") return undefined;
    let current = true;
    const available = driving && liveNavigation.active && liveNavigation.canRepeat;
    onRepeatActionChange(available ? () => {
      if (current) return liveNavigation.repeatLastChallenge();
    } : null);
    return () => {
      current = false;
      onRepeatActionChange(null);
    };
  }, [driving, lessonEventId, onRepeatActionChange, liveNavigation.active,
    liveNavigation.canRepeat, liveNavigation.repeatLastChallenge]);

  // Continuous fixes remain transient. Work Mode receives only the latest
  // in-memory context so evidence can attach that moment. No continuous
  // journey trace is persisted; mapped challenge encounters are discrete.
  useEffect(() => {
    if (surface !== "work_mode" || typeof onNavigationContextChangeRef.current !== "function") return;
    onNavigationContextChangeRef.current({
      active: liveNavigation.active,
      device: liveNavigation.device,
      requesting: liveNavigation.requesting,
      rerouting: liveNavigation.rerouting,
      error: liveNavigation.error,
      hasRoute: parseRouteGeometry(liveNavigation.routeGeometry).length >= 2
        || (liveNavigation.nativeMode && liveNavigation.active),
      lastCompletedChallenge: liveNavigation.lastCompletedChallenge,
      destinationPoint: liveNavigation.destinationPoint,
      routeProvider: routeProvider || "google_routes",
    });
  }, [
    surface,
    routeProvider,
    liveNavigation.device,
    liveNavigation.active,
    liveNavigation.requesting,
    liveNavigation.rerouting,
    liveNavigation.error,
    liveNavigation.routeGeometry,
    liveNavigation.nativeMode,
    liveNavigation.lastCompletedChallenge,
    liveNavigation.destinationPoint,
  ]);

  useEffect(() => {
    if (typeof onNavigationControlChangeRef.current !== "function") return undefined;
    onNavigationControlChangeRef.current({
      active: liveNavigation.active,
      requesting: liveNavigation.requesting,
      stop: liveNavigation.stop,
      toggle: liveNavigation.toggle,
      retryRoute: liveNavigation.retryRoute,
      applyPart3Route: liveNavigation.applyPart3Route,
    });
    return undefined;
  }, [
    liveNavigation.active,
    liveNavigation.requesting,
    liveNavigation.stop,
    liveNavigation.toggle,
    liveNavigation.retryRoute,
    liveNavigation.applyPart3Route,
  ]);

  // Batch S2 · educational environment overlay state.
  const [environments, setEnvironments] = useState([]);
  const [envMeta, setEnvMeta] = useState({ count: 0, capped: false });
  const [envError, setEnvError] = useState(null);
  const [showOverlay, setShowOverlay] = useState(true);
  // S2 · Batch S2 UX revision — filters collapse behind a single
  // control on both mobile and desktop, so the map stays the primary
  // working surface. Founder Blueprint: the map is a workspace, not
  // a dashboard; controls must never permanently cover the map or
  // its markers.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [enabledCats, setEnabledCats] = useState(() => new Set(CATEGORY_KEYS));
  const boundsRef = useRef(null);

  // Batch S3 · Capture Teaching Place — instructor drops a marker,
  // sheet opens, single write via POST /api/spatial/environments.
  const [captureMode, setCaptureMode] = useState(false);
  const [captureDraft, setCaptureDraft] = useState(null); // { latitude, longitude }
  const [captureForm, setCaptureForm] = useState({
    category: "junction", level: Number(preferredTeachingLevel) || 1,
    title: "", description: "", hazards: "",
    prerequisite_skills: "", confidence_expectation: "",
  });
  const [saving, setSaving] = useState(false);

  // Batch S4 · Environment Detail Sheet — canonical presentation
  // surface for one environment. Docked; map remains visible behind.
  const [detail, setDetail] = useState(null);   // full envelope
  const [detailEditing, setDetailEditing] = useState(false);
  const [detailForm, setDetailForm] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);

  // Batch S5 · Move Teaching Place — instructor relocates a marker
  // they own. Docked confirm/cancel bar; canonical coords-only POST.
  const [moveMode, setMoveMode] = useState(false);
  const [moveDraft, setMoveDraft] = useState(null); // { latitude, longitude }
  const [moveBusy, setMoveBusy] = useState(false);

  // Batch S6 · Post-Lesson Reflection Capture — docked inline panel
  // appended off the Detail Sheet. Reflections are append-only and
  // live in a separate collection; the environment is never mutated.
  const [reflectMode, setReflectMode] = useState(false);
  const [reflectBusy, setReflectBusy] = useState(false);
  const [reflectForm, setReflectForm] = useState({
    lesson_id: "", learner_id: "",
    confidence: "", outcome: "", notes: "",
    hazards_observed: "",
    worked_well: "", needs_improvement: "",
  });

  // Batch S7 · Reflection Timeline (read-only history). Load newest
  // first for the currently-selected owned+active environment.
  const [reflectionHistory, setReflectionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const load = useCallback(async (deviceOverride) => {
    if (isPart3Journey) {
      // This map is a navigation surface only. Ordinary pupil/lesson spatial
      // context is deliberately absent from the one-off Part 3 session.
      setCtx(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (lessonEventId) params.lesson_event_id = lessonEventId;
      const src = deviceOverride ?? device;
      if (src?.latitude != null && src?.longitude != null) {
        params.device_latitude  = src.latitude;
        params.device_longitude = src.longitude;
        params.device_permission = "granted";
      } else if (src?.permission === "denied") {
        params.device_permission = "denied";
      }
      const r = await axios.get(`${API}/api/spatial/context`, {
        withCredentials: true,
        params,
      });
      setCtx(r.data);
    } catch {
      setError("Couldn't load spatial context.");
    } finally {
      setLoading(false);
    }
  }, [device, lessonEventId, isPart3Journey]);

  useEffect(() => { load(); }, []);

  // Batch S2 · fetch educational environments for the visible view.
  // Deterministic: same bbox + same category set → same response.
  const fetchEnvironments = useCallback(async (bbox) => {
    if (isPart3Journey) return;
    if (!bbox) return;
    boundsRef.current = bbox;
    setEnvError(null);
    try {
      const cats = Array.from(enabledCats);
      const params = {
        min_lat: bbox.min_lat, min_lng: bbox.min_lng,
        max_lat: bbox.max_lat, max_lng: bbox.max_lng,
      };
      if (cats.length && cats.length < CATEGORY_KEYS.length) {
        params.categories = cats.join(",");
      }
      const r = await axios.get(`${API}/api/spatial/environments`, {
        withCredentials: true, params,
      });
      setEnvironments(Array.isArray(r.data?.environments) ? r.data.environments : []);
      setEnvMeta({
        count:  r.data?.count || 0,
        capped: (r.data?.count || 0) >= (r.data?.meta?.capped_at || 200),
      });
    } catch {
      setEnvError("Couldn't load educational environments.");
      setEnvironments([]);
    }
  }, [enabledCats, isPart3Journey]);

  // Refetch when the category filter changes and we already have bounds.
  useEffect(() => {
    if (boundsRef.current) fetchEnvironments(boundsRef.current);
  }, [enabledCats, fetchEnvironments]);

  const toggleCat = useCallback((c) => {
    setEnabledCats((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  }, []);

  // Batch S3 · capture handlers.
  const startCapture = useCallback(() => {
    setCaptureMode(true);
    setCaptureDraft(null);
    setCaptureForm((current) => ({
      ...current,
      level: Number(preferredTeachingLevel) || current.level || 1,
    }));
    toast(`Tap the map to confirm the Level ${Number(preferredTeachingLevel) || 1} teaching area`);
  }, [preferredTeachingLevel]);

  const cancelCapture = useCallback(() => {
    setCaptureMode(false);
    setCaptureDraft(null);
    setCaptureForm({
      category: "junction", level: Number(preferredTeachingLevel) || 1,
      title: "", description: "", hazards: "",
      prerequisite_skills: "", confidence_expectation: "",
    });
  }, [preferredTeachingLevel]);

  const submitCapture = useCallback(async () => {
    if (!captureDraft || saving) return;
    setSaving(true);
    try {
      const payload = {
        category: captureForm.category,
        level:    Number(captureForm.level),
        latitude:  captureDraft.latitude,
        longitude: captureDraft.longitude,
        title:                  captureForm.title.trim() || null,
        description:            captureForm.description.trim() || null,
        hazards:                captureForm.hazards
                                  ? captureForm.hazards.split(",").map((s) => s.trim()).filter(Boolean)
                                  : null,
        prerequisite_skills:    captureForm.prerequisite_skills
                                  ? captureForm.prerequisite_skills.split(",").map((s) => s.trim()).filter(Boolean)
                                  : null,
        confidence_expectation: captureForm.confidence_expectation.trim() || null,
      };
      await axios.post(`${API}/api/spatial/environments`, payload, {
        withCredentials: true,
      });
      toast.success("Teaching place saved");
      cancelCapture();
      // Refresh the overlay immediately so the new marker appears.
      if (boundsRef.current) fetchEnvironments(boundsRef.current);
      try {
        await onEnvironmentSaved?.();
      } catch {
        // The place is already saved. A later visibility refresh will
        // recover the route plan if this immediate refresh is interrupted.
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save that teaching place";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [captureDraft, captureForm, cancelCapture, fetchEnvironments, onEnvironmentSaved, saving]);

  // Batch S7 · Reflection Timeline loader. Declared BEFORE openDetail
  // so React's TDZ doesn't trip when the consumer callback captures
  // it in its dependency array.
  const loadReflectionHistory = useCallback(async (envId) => {
    if (!envId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const r = await axios.get(
        `${API}/api/spatial/environments/${encodeURIComponent(envId)}/reflections`,
        { withCredentials: true },
      );
      setReflectionHistory(Array.isArray(r.data?.reflections) ? r.data.reflections : []);
    } catch {
      setHistoryError("Couldn't load reflection history.");
      setReflectionHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Batch S4 · Detail Sheet handlers.
  const openDetail = useCallback(async (envId) => {
    setDetailEditing(false);
    setDetail(null);
    setReflectionHistory([]);
    setHistoryError(null);
    try {
      const r = await axios.get(
        `${API}/api/spatial/environments/${encodeURIComponent(envId)}`,
        { withCredentials: true },
      );
      setDetail(r.data);
      // Batch S7 · load timeline only when the caller owns the row
      // and it is active. The backend refuses everything else with
      // a 404, so we don't fire the request pointlessly.
      if (r.data?.source === "instructor_confirmed" && r.data?.status !== "retired") {
        loadReflectionHistory(r.data.id);
      }
    } catch {
      toast.error("Couldn't load that teaching place");
    }
  }, [loadReflectionHistory]);

  const closeDetail = useCallback(() => {
    setDetail(null);
    setDetailEditing(false);
    setDetailForm(null);
    setMoveMode(false);
    setMoveDraft(null);
    setReflectMode(false);
    setReflectionHistory([]);
    setHistoryError(null);
  }, []);

  const startEdit = useCallback(() => {
    if (!detail) return;
    setDetailForm({
      category:               detail.category,
      title:                  detail.title || "",
      level:                  detail.level || 1,
      description:            detail.description || "",
      hazards:                (detail.hazards || []).join(", "),
      prerequisite_skills:    (detail.prerequisite_skills || []).join(", "),
      confidence_expectation: detail.confidence_expectation || "",
    });
    setDetailEditing(true);
  }, [detail]);

  const saveEdit = useCallback(async () => {
    if (!detail || !detailForm || detailBusy) return;
    setDetailBusy(true);
    try {
      const payload = {
        category:               detailForm.category,
        level:                  Number(detailForm.level),
        title:                  detailForm.title.trim() || null,
        description:            detailForm.description.trim() || null,
        hazards:                detailForm.hazards
                                  ? detailForm.hazards.split(",").map((s) => s.trim()).filter(Boolean)
                                  : [],
        prerequisite_skills:    detailForm.prerequisite_skills
                                  ? detailForm.prerequisite_skills.split(",").map((s) => s.trim()).filter(Boolean)
                                  : [],
        confidence_expectation: detailForm.confidence_expectation.trim() || null,
      };
      const r = await axios.patch(
        `${API}/api/spatial/environments/${encodeURIComponent(detail.id)}`,
        payload, { withCredentials: true },
      );
      setDetail(r.data);
      setDetailEditing(false);
      toast.success("Teaching place updated");
      if (boundsRef.current) fetchEnvironments(boundsRef.current);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save changes";
      toast.error(msg);
    } finally {
      setDetailBusy(false);
    }
  }, [detail, detailForm, detailBusy, fetchEnvironments]);

  const retireDetail = useCallback(async () => {
    if (!detail || detailBusy) return;
    setDetailBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/spatial/environments/${encodeURIComponent(detail.id)}/retire`,
        {}, { withCredentials: true },
      );
      setDetail(r.data);
      toast.success("Teaching place retired");
      if (boundsRef.current) fetchEnvironments(boundsRef.current);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't retire";
      toast.error(msg);
    } finally {
      setDetailBusy(false);
    }
  }, [detail, detailBusy, fetchEnvironments]);

  // Batch S5 · Move Teaching Place handlers.
  const startMove = useCallback(() => {
    if (!detail) return;
    setDetailEditing(false);
    setDetailForm(null);
    setMoveDraft(null);
    setMoveMode(true);
    toast("Tap the map at the corrected location");
  }, [detail]);

  const cancelMove = useCallback(() => {
    setMoveMode(false);
    setMoveDraft(null);
  }, []);

  const confirmMove = useCallback(async () => {
    if (!detail || !moveDraft || moveBusy) return;
    setMoveBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/spatial/environments/${encodeURIComponent(detail.id)}/move`,
        { latitude: moveDraft.latitude, longitude: moveDraft.longitude },
        { withCredentials: true },
      );
      setDetail(r.data);
      setMoveMode(false);
      setMoveDraft(null);
      toast.success("Teaching place moved");
      if (boundsRef.current) fetchEnvironments(boundsRef.current);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't move that teaching place";
      toast.error(msg);
    } finally {
      setMoveBusy(false);
    }
  }, [detail, moveDraft, moveBusy, fetchEnvironments]);

  // Batch S6 · Post-Lesson Reflection Capture handlers.
  const startReflect = useCallback(() => {
    if (!detail) return;
    setDetailEditing(false);
    setDetailForm(null);
    setMoveMode(false);
    setMoveDraft(null);
    setReflectForm({
      lesson_id: "", learner_id: "",
      confidence: "", outcome: "", notes: "",
      hazards_observed: "",
      worked_well: "", needs_improvement: "",
    });
    setReflectMode(true);
  }, [detail]);

  const cancelReflect = useCallback(() => {
    setReflectMode(false);
  }, []);

  const submitReflection = useCallback(async () => {
    if (!detail || reflectBusy) return;
    setReflectBusy(true);
    try {
      const reflectionPayload = {
        lesson_id:          reflectForm.lesson_id.trim() || null,
        learner_id:         reflectForm.learner_id.trim() || null,
        confidence:         reflectForm.confidence.trim() || null,
        outcome:            reflectForm.outcome.trim() || null,
        notes:              reflectForm.notes.trim() || null,
        hazards_observed:   reflectForm.hazards_observed
                              ? reflectForm.hazards_observed.split(",").map((s) => s.trim()).filter(Boolean)
                              : [],
        worked_well:        reflectForm.worked_well.trim() || null,
        needs_improvement:  reflectForm.needs_improvement.trim() || null,
      };
      await axios.post(
        `${API}/api/spatial/environments/${encodeURIComponent(detail.id)}/reflections`,
        reflectionPayload, { withCredentials: true },
      );
      setReflectMode(false);
      toast.success("Reflection saved");
      // Batch S7 · refresh the timeline so the new entry shows
      // immediately without reloading the whole page.
      loadReflectionHistory(detail.id);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save that reflection";
      toast.error(msg);
    } finally {
      setReflectBusy(false);
    }
  }, [detail, reflectForm, reflectBusy, loadReflectionHistory]);

  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setDevice({ status: "unavailable", latitude: null, longitude: null, permission: "unavailable", requestedAt: Date.now() });
      return;
    }
    setDevice((d) => ({ ...d, status: "requesting", requestedAt: Date.now() }));
    setReadiness(null);
    setReadinessError(null);
    const handlePosition = async (pos) => {
      const next = {
        status: "active",
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        permission: "granted",
        requestedAt: Date.now(),
      };
      setDevice(next);
      await load(next);
      if (lessonEventId && !isPart3Journey) {
        try {
          const result = await axios.get(
            `${API}/api/run-my-day/lessons/${encodeURIComponent(lessonEventId)}/readiness`,
            {
              withCredentials: true,
              params: {
                device_latitude: next.latitude,
                device_longitude: next.longitude,
              },
            },
          );
          setReadiness(result.data);
        } catch {
          setReadinessError("Google could not complete the readiness check.");
        }
      }
    };
    const handleError = (err) => {
      const denied = err?.code === 1 || /denied/i.test(String(err?.message || err));
      const next = {
        status: denied ? "denied" : "unavailable",
        latitude: null, longitude: null,
        permission: denied ? "denied" : "unavailable",
        requestedAt: Date.now(),
      };
      setDevice(next);
      setReadinessError(err?.message || null);
      load(next);
    };
    if (surface === "work_mode") {
      requestFreshRouteLocation().then((location) => handlePosition({
        coords: { latitude: location.latitude, longitude: location.longitude },
      })).catch(handleError);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      handleError,
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 },
    );
  }, [load, lessonEventId, surface, isPart3Journey]);

  const pickup   = ctx?.pickup;
  const dropoff  = ctx?.dropoff;
  const activeRouteGeometry = (
    surface === "work_mode" ? liveNavigation.routeGeometry : routeGeometry
  );
  const routePoints = useMemo(() => parseRouteGeometry(activeRouteGeometry), [activeRouteGeometry]);
  const readinessRoutePoints = useMemo(
    () => parseRouteGeometry(readiness?.route_geometry), [readiness?.route_geometry]);
  const automaticTeachingPins = useMemo(
    () => (Array.isArray(teachingChallenges) ? teachingChallenges : []).filter(
      (item) => Number.isFinite(Number(item?.latitude)) && Number.isFinite(Number(item?.longitude)),
    ),
    [teachingChallenges],
  );
  // Keep the combined geometry referentially stable. Passing a newly-created
  // array into FitToContext on every render caused fitBounds -> moveend ->
  // environment fetch -> render to repeat indefinitely.
  const fittedRoutePoints = useMemo(
    () => surface === "work_mode" && driving
      ? routePoints
      : [
        ...routePoints,
        ...readinessRoutePoints,
        ...automaticTeachingPins.map((item) => [Number(item.latitude), Number(item.longitude)]),
      ],
    [surface, driving, routePoints, readinessRoutePoints, automaticTeachingPins],
  );
  const devCtx = (
    surface === "work_mode" && liveNavigation.device.status !== "idle"
      ? liveNavigation.device
      : ctx?.device
  );
  const focus    = ctx?.focus;
  const objective = ctx?.objective;

  const hasAnyPoint = useMemo(() => {
    const any = (p) => p && p.latitude != null && p.longitude != null;
    return any(pickup) || any(dropoff) || any(devCtx) || routePoints.length > 1 || automaticTeachingPins.length > 0;
  }, [pickup, dropoff, devCtx, routePoints, automaticTeachingPins]);

  const liveMode = surface === "work_mode" && routePoints.length > 1;
  const deviceLabel = useMemo(() => {
    if (liveMode) {
      if (liveNavigation.requesting) return "Locating…";
      if (liveNavigation.active) return "Pause";
      if (liveNavigation.device.status === "denied") return "Try location again";
      return "Start navigation";
    }
    if (device.status === "requesting") return "Locating…";
    if (device.status === "active")     return "Location active";
    if (device.status === "denied")     return "Try location again";
    if (device.status === "unavailable")return "Try location again";
    return "Go";
  }, [
    device.status,
    liveMode,
    liveNavigation.active,
    liveNavigation.device.status,
    liveNavigation.requesting,
  ]);

  const handleGo = liveMode ? liveNavigation.toggle : requestLocation;
  const goDisabled = liveMode
    ? liveNavigation.requesting
    : device.status === "requesting";

  const searchStopPlaces = async (event, queryOverride = null, isCurrent = () => true) => {
    event?.preventDefault?.();
    const scope = destinationSearchScopeRef.current;
    const request = destinationVoiceRequestRef.current;
    const current = () => scope && destinationSearchScopeRef.current === scope
      && destinationVoiceRequestRef.current === request && isCurrent();
    const query = (typeof queryOverride === "string" ? queryOverride : stopSearch).trim();
    const trace = (name, outcome, details = {}) => { try { onJourneyCheckpoint?.(name, outcome, details); } catch (_) { /* tracing never blocks search */ } };
    if (!current() || !query || liveNavigation.rerouting || stopSearchBusy) return;
    destinationConfirmationRef.current.cancel();
    setStopActionError(null);
    setSelectedStop(null);
    setStopPreview(null);
    setStopSearchBusy(true);
    trace("destination_search_request", "started");
    try {
      const fix = liveNavigation.device;
      const hasLocation = Number.isFinite(Number(fix?.latitude))
        && Number.isFinite(Number(fix?.longitude));
      const response = await axios.post(
        `${API}/api/run-my-day/places/search`,
        {
          query,
          ...(hasLocation ? {
            latitude: Number(fix.latitude),
            longitude: Number(fix.longitude),
          } : {}),
          limit: 5,
        },
        { withCredentials: true },
      );
      if (!current()) return null;
      trace("destination_search_response", "ok", { status_code: response.status });
      if (response.data?.provider !== destinationSearchProvider(query) || !Array.isArray(response.data?.places)) {
        throw new Error(isTestCentreQuery(query)
          ? "The verified NDG test-centre catalogue is unavailable. No general place search was used."
          : "Google place search returned an unusable result.");
      }
      setStopPlaces(response.data.places);
      if (!response.data.places.length) {
        trace("destination_search_result", "failed", { error_code: "no_results" });
        setStopActionError("No matching places were found. Try a nearby town or a more specific name.");
      } else {
        trace("destination_search_result", "ok");
      }
      return response.data.places;
    } catch (requestError) {
      if (!current()) return null;
      const statusCode = requestError?.response?.status;
      const providerCode = requestError?.response?.data?.detail?.code;
      const errorCode = providerCode === "PLACE_SEARCH_UNAVAILABLE" ? "place_search_unavailable"
        : providerCode === "CENTRE_CATALOGUE_UNAVAILABLE" ? "centre_catalogue_unavailable"
          : requestError?.message?.includes("unusable result") ? "invalid_response"
            : Number.isInteger(statusCode) ? "http_error" : "unknown";
      trace("destination_search_result", "failed", {
        ...(Number.isInteger(statusCode) ? { status_code: statusCode } : {}),
        error_code: errorCode,
      });
      setStopPlaces([]);
      setStopActionError(
        requestError?.response?.data?.detail?.message
        || requestError?.response?.data?.detail
        || requestError?.message
        || "Place search is unavailable. Your current route remains active.",
      );
    } finally {
      if (current()) setStopSearchBusy(false);
    }
  };

  const startDestinationVoiceSearch = () => {
    if (stopVoiceRecognitionRef.current) {
      const activeRecognition = stopVoiceRecognitionRef.current;
      stopVoiceRecognitionRef.current = null;
      activeRecognition.onresult = null;
      activeRecognition.onend = null;
      activeRecognition.onerror = null;
      try { activeRecognition.abort(); } catch {}
      setStopVoiceListening(false);
      return;
    }

    const Recognition = typeof window !== "undefined"
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
    if (!Recognition) {
      setStopActionError("Voice destination search isn't available in this browser. You can type the place instead.");
      return;
    }

    const recognition = new Recognition();
    recognition.lang = "en-GB";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      if (stopVoiceRecognitionRef.current !== recognition || !searchModeOpen) return;
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      const query = normalizeDestinationVoiceTranscript(transcript);
      if (!query) {
        setStopActionError("I didn't catch a place. Please try again or type the destination.");
        return;
      }
      setStopActionError(null);
      setStopSearch(query);
      setStopPlaces([]);
      setSelectedStop(null);
      setStopPreview(null);
      void searchStopPlaces(null, query);
    };
    recognition.onerror = () => {
      if (stopVoiceRecognitionRef.current !== recognition) return;
      stopVoiceRecognitionRef.current = null;
      setStopVoiceListening(false);
      setStopActionError("I couldn't hear that destination. Try again or type it.");
    };
    recognition.onend = () => {
      if (stopVoiceRecognitionRef.current !== recognition) return;
      stopVoiceRecognitionRef.current = null;
      setStopVoiceListening(false);
    };
    stopVoiceRecognitionRef.current = recognition;
    setStopActionError(null);
    setStopVoiceListening(true);
    try {
      recognition.start();
    } catch {
      stopVoiceRecognitionRef.current = null;
      setStopVoiceListening(false);
      setStopActionError("Voice destination search couldn't start. Try again or type the place.");
    }
  };

  const stopDestinationVoiceSearch = () => {
    const recognition = stopVoiceRecognitionRef.current;
    stopVoiceRecognitionRef.current = null;
    setStopVoiceListening(false);
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onend = null;
    recognition.onerror = null;
    try { recognition.abort(); } catch {}
  };

  const previewStopPlace = async (place, isCurrent = () => true, preserveConfirmation = false) => {
    const scope = destinationSearchScopeRef.current;
    const request = destinationVoiceRequestRef.current;
    const current = () => scope && destinationSearchScopeRef.current === scope
      && destinationVoiceRequestRef.current === request && isCurrent();
    if (!current() || !place?.place_id || !lessonEventId || stopPreviewBusy) return;
    if (!preserveConfirmation) destinationConfirmationRef.current.cancel();
    const fix = liveNavigation.device;
    const capturedAt = Number(fix?.requestedAt);
    const ageMs = Date.now() - capturedAt;
    if (
      fix?.status !== "active"
      || !Number.isFinite(Number(fix?.latitude))
      || !Number.isFinite(Number(fix?.longitude))
      || !Number.isFinite(capturedAt)
      || ageMs < -5000
      || ageMs > 30000
    ) {
      setStopActionError("A fresh GPS position is needed to preview this route. Keep navigation running and try again.");
      return;
    }
    setStopActionError(null);
    setSelectedStop(place);
    setStopPreview(null);
    setStopPreviewBusy(true);
    try {
      const response = await axios.post(
        `${API}/api/run-my-day/lessons/${encodeURIComponent(lessonEventId)}/places/route-preview`,
        {
          place_id: place.place_id,
          current_location: {
            latitude: Number(fix.latitude),
            longitude: Number(fix.longitude),
            accuracy_metres: Number.isFinite(Number(fix.accuracy)) ? Number(fix.accuracy) : null,
            captured_at: new Date(capturedAt).toISOString(),
          },
        },
        { withCredentials: true },
      );
      if (!current()) return null;
      const result = response.data;
      if (!result || !["OK", "RETURN_DEADLINE_AT_RISK"].includes(result.status)) {
        throw new Error(result?.reason || "A safe route preview could not be proved.");
      }
      setSelectedStop(result.destination);
      setStopPreview(result);
      return result;
    } catch (requestError) {
      if (!current()) return null;
      setStopPreview(null);
      setStopActionError(
        requestError?.response?.data?.detail?.message
        || requestError?.response?.data?.detail
        || requestError?.message
        || "A safe route preview could not be proved. Your current route remains active.",
      );
    } finally {
      if (current()) setStopPreviewBusy(false);
    }
  };

  const openStopSearch = () => {
    setSearchModeOpen(true);
    onSearchModeChange?.(true);
  };

  const closeStopSearch = (preserveVoiceRequest = false) => {
    if (destinationCommitInFlightRef.current && preserveVoiceRequest !== true) return;
    if (preserveVoiceRequest !== true) {
      destinationVoiceRequestRef.current++;
      destinationVoiceBusyRef.current = false;
      setStopSearchBusy(false);
      setStopPreviewBusy(false);
      setSelectedStop(null);
      setStopPreview(null);
      setStopPlaces([]);
    }
    destinationConfirmationRef.current.cancel();
    stopDestinationVoiceSearch();
    setSearchModeOpen(false);
    onSearchModeChange?.(false);
  };

  const goToSelectedStop = async (placeOverride = null, previewOverride = null, isCurrent = () => true) => {
    const scope = destinationSearchScopeRef.current;
    const request = destinationVoiceRequestRef.current;
    const current = () => scope && destinationSearchScopeRef.current === scope
      && destinationVoiceRequestRef.current === request && isCurrent();
    const place = placeOverride?.place_id ? placeOverride : selectedStop;
    const preview = previewOverride?.status ? previewOverride : stopPreview;
    if (
      !current()
      || !place?.place_id
      || !preview
      || !["OK", "RETURN_DEADLINE_AT_RISK"].includes(preview.status)
      || liveNavigation.rerouting
      || destinationCommitInFlightRef.current
    ) return false;
    const commit = {};
    destinationCommitInFlightRef.current = commit;
    try {
      const routeAccepted = await liveNavigation.goToDestination({
        name: place.name,
        place_id: place.place_id,
        latitude: Number(place.latitude),
        longitude: Number(place.longitude),
      });
      if (routeAccepted && current()) closeStopSearch(true);
      return routeAccepted === true && current();
    } finally {
      if (destinationCommitInFlightRef.current === commit) destinationCommitInFlightRef.current = false;
    }
  };

  destinationVoiceHandlerRef.current = async (command, scopeIsCurrent) => {
    if (command?.action === "cancel_destination") {
      if (destinationCommitInFlightRef.current) return { ok: false,
        message: "The confirmed route change is already in progress. Check the map when it finishes." };
      closeStopSearch();
      return { ok: true, message: "Destination request cancelled. The current route is unchanged." };
    }
    if (destinationVoiceBusyRef.current || stopSearchBusy || stopPreviewBusy || liveNavigation.rerouting) {
      return { ok: false, message: "The map is already updating. Wait for that request to finish." };
    }
    if (!scopeIsCurrent()) return { ok: false, message: "That lesson map is no longer active." };
    const request = ++destinationVoiceRequestRef.current;
    const isCurrent = () => scopeIsCurrent() && destinationVoiceRequestRef.current === request;
    destinationVoiceBusyRef.current = true;
    try {
      if (command?.action === "confirm_destination") {
        return await destinationConfirmationRef.current.confirm({ isCurrent,
          preview: place => previewStopPlace(place, isCurrent, true),
          go: (place, preview) => goToSelectedStop(place, preview, isCurrent),
        });
      }
      const query = command?.destination_query;
      if (!query || typeof query !== "string") return { ok: false, message: "Which destination would you like?" };
      openStopSearch();
      setStopSearch(query);
      return await runDestinationCommand({ query, isCurrent,
        search: value => searchStopPlaces(null, value, isCurrent),
        preview: place => previewStopPlace(place, isCurrent),
        confirmation: destinationConfirmationRef.current,
      });
    } finally {
      if (isCurrent()) destinationVoiceBusyRef.current = false;
    }
  };

  useEffect(() => {
    if (typeof onDestinationActionChange !== "function" || !driving || surface !== "work_mode"
        || routeMode !== "lesson" || !lessonEventId) return undefined;
    const scope = {};
    destinationVoiceScopeRef.current = scope;
    const isCurrent = () => destinationVoiceScopeRef.current === scope;
    onDestinationActionChange(query => isCurrent()
      ? destinationVoiceHandlerRef.current(query, isCurrent)
      : Promise.resolve({ ok: false, message: "That lesson map is no longer active." }));
    return () => {
      if (isCurrent()) destinationVoiceScopeRef.current = null;
      destinationConfirmationRef.current.cancel();
      onDestinationActionChange(null);
    };
  }, [driving, surface, routeMode, lessonEventId, onDestinationActionChange]);

  return (
    <section
      data-testid="contextual-map"
      data-surface={surface}
      data-driving={driving ? "true" : "false"}
      className={`${driving ? "relative h-full bg-transparent [&_.leaflet-bottom]:bottom-1/2" : "rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"} overflow-hidden ${className}`}
    >
      {surface === "work_mode" && routeMode === "lesson" && lessonEventId
        && !searchModeOpen && controlsVisible && (
        <button
          type="button"
          data-testid="lesson-location-search-trigger"
          aria-label="Search for a destination"
          onClick={openStopSearch}
          className="absolute left-3 right-3 top-3 z-40 mx-auto flex min-h-11 max-w-sm items-center justify-center rounded-full border border-white/40 bg-blue-950/90 px-5 text-sm font-bold text-white shadow-lg backdrop-blur-sm"
        >
          Search destination
        </button>
      )}

      {/* Compact context strip */}
      {!driving && <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 dark:text-white">
          <MapPin className="w-4 h-4" />
          {focus?.learner_name || (loading ? "Loading…" : "No lesson in view")}
        </span>
        {focus?.lesson_start && (
          <span data-testid="contextual-map-times" className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3.5 h-3.5" />
            {fmtTime(focus.lesson_start)} → {fmtTime(focus.scheduled_finish)}
            {focus.remaining_minutes != null && (
              <span className="ml-1 text-slate-400">· {fmtMinutes(focus.remaining_minutes)} left</span>
            )}
          </span>
        )}
        {objective && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Compass className="w-3.5 h-3.5" />
            {objective}
          </span>
        )}
      </div>}

      <div data-ndg-map-overlay={driving ? "" : undefined} className={driving ? "absolute top-20 left-3 right-3 z-10 max-h-[35%] overflow-y-auto rounded-xl bg-slate-950/75 text-white backdrop-blur-sm" : undefined}>
      {!driving && (readiness || readinessError) && (
        <div
          data-testid="lesson-readiness-result"
          data-status={readiness?.status || "ERROR"}
          role={["AT_PICKUP", "ON_TIME", "LATE_BUT_POSSIBLE"].includes(readiness?.status) ? "status" : "alert"}
          className={`mx-3 mt-3 rounded-xl border px-3 py-3 ${
            readiness?.status === "TOO_FAR"
              ? "border-rose-300 bg-rose-50 text-rose-900"
              : readiness?.status === "AT_PICKUP" || readiness?.status === "ON_TIME"
                ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
        >
          <p className="text-sm font-black">
            {readiness?.message || readinessError}
          </p>
          {readiness && (
            <>
              <p className="mt-1 text-xs font-semibold">
                {readiness.straight_line_km != null ? `${readiness.straight_line_km} km from pickup` : ""}
                {readiness.travel_minutes != null ? ` · ${readiness.travel_minutes} min by Google` : ""}
              </p>
              {readiness.diagnostic?.provider_reason_code && (
                <p className="mt-1 text-xs font-semibold" data-testid="pickup-readiness-provider-reason">
                  Provider result: {readiness.diagnostic.provider_reason_code}
                </p>
              )}
              {readiness.diagnostic?.reference_id && (
                <p className="mt-1 text-xs font-semibold" data-testid="pickup-readiness-reference">
                  Reference: {readiness.diagnostic.reference_id}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {liveMode
        && !liveNavigation.active
        && !liveNavigation.requesting
        && liveNavigation.device.status !== "denied"
        && (
          <div className="border-b border-blue-800 bg-blue-950 px-4 py-3">
            <button
              type="button"
              data-testid="start-live-navigation-btn"
              onClick={liveNavigation.toggle}
              className="min-h-14 w-full rounded-xl bg-blue-500 px-4 text-base font-black text-white"
            >
              Start live navigation from my location
            </button>
          </div>
        )}

      {liveMode
        && !liveNavigation.active
        && !liveNavigation.requesting
        && liveNavigation.device.status === "denied"
        && (
          <div
            data-testid="live-navigation-location-denied"
            className="border-b border-amber-700 bg-amber-950 px-4 py-3 text-amber-50"
          >
            <p className="text-sm font-black">Safari has blocked location.</p>
            <p className="mt-1 text-xs text-amber-100">
              Open this page’s Website Settings, set Location to Allow, then try again.
            </p>
            <button
              type="button"
              data-testid="retry-live-navigation-location-btn"
              onClick={liveNavigation.toggle}
              className="mt-3 min-h-11 w-full rounded-lg bg-amber-400 px-4 text-sm font-black text-slate-950"
            >
              Try location again
            </button>
          </div>
        )}

      {liveMode && (liveNavigation.active || liveNavigation.requesting || liveNavigation.error) && (
        <div
          data-testid="live-google-navigation"
          className={`text-white px-4 py-3 ${driving ? "" : "border-b border-slate-200 dark:border-slate-800 bg-blue-950"}`}
        >
          <div className="flex items-start gap-3">
            <Navigation className="w-6 h-6 mt-0.5 text-blue-300 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wider font-bold text-blue-300">
                {liveNavigation.rerouting ? "Updating route" : liveNavigation.status}
              </p>
              <p className="text-lg font-black leading-snug mt-0.5">
                {liveNavigation.currentInstruction?.message
                  || (liveNavigation.requesting ? "Finding your position…" : "Follow the highlighted route")}
              </p>
              {liveNavigation.distanceToInstruction != null && (
                <p className="text-sm font-semibold text-blue-200 mt-1">
                  {formatNavigationDistance(liveNavigation.distanceToInstruction)}
                </p>
              )}
              {liveNavigation.error && (
                <p className="text-sm text-amber-300 mt-1">{liveNavigation.error}</p>
              )}
            </div>
            <button
              type="button"
              data-testid="live-navigation-mute"
              aria-label={liveNavigation.muted ? "Turn spoken directions on" : "Mute spoken directions"}
              onClick={() => liveNavigation.setMuted(!liveNavigation.muted)}
              className="min-h-11 min-w-11 rounded-lg border border-blue-700 flex items-center justify-center"
            >
              {liveNavigation.muted
                ? <VolumeX className="w-5 h-5" />
                : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}



      {driving && liveMode && liveNavigation.canRepeat && !onRepeatActionChange && (
        <button type="button" data-testid="repeat-last-challenge-btn"
          onClick={liveNavigation.repeatLastChallenge}
          className="m-2 inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/30 px-3 text-sm font-semibold"
          aria-label="Repeat the last driving challenge">
          <RotateCcw className="h-4 w-4" />Repeat
        </button>
      )}
      </div>

      {surface === "work_mode" && routeMode === "lesson" && lessonEventId && searchModeOpen && (
        <section
          data-testid="run-day-stop-search"
          aria-label={isTestCentreQuery(stopSearch) ? "Verified NDG test-centre search" : "Google Maps destination search"}
          className={`mx-2 mb-2 rounded-xl border border-blue-700 bg-blue-950/95 p-3 text-white shadow-lg ${driving ? "absolute inset-x-3 top-16 z-50 max-h-[85%] overflow-y-auto" : "border-b"}`}
        >
          {driving && (
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-blue-100">{isTestCentreQuery(stopSearch) ? "Verified NDG test centres" : "Google Maps destination search"}</p>
              <button
                type="button"
                data-testid="lesson-location-search-close"
                aria-label="Close destination search"
                disabled={liveNavigation.rerouting}
                onClick={closeStopSearch}
                className="min-h-11 rounded-lg border border-white/30 px-3 text-sm font-semibold text-white"
              >
                Back to lesson
              </button>
            </div>
          )}
          <form role="search" onSubmit={searchStopPlaces}>
            <label
              htmlFor="run-day-stop-query"
              className="mb-2 block text-xs font-bold uppercase tracking-wide text-blue-200"
            >
              Search for a place
            </label>
            <div className="flex gap-2">
              <input
                id="run-day-stop-query"
                data-testid="run-day-stop-query"
                value={stopSearch}
                onChange={(event) => {
                  destinationVoiceRequestRef.current++;
                  destinationConfirmationRef.current.cancel();
                  setStopSearch(event.target.value);
                  setStopPlaces([]);
                  setSelectedStop(null);
                  setStopPreview(null);
                  setStopActionError(null);
                }}
                placeholder="e.g. McDonald's, Peterborough"
                maxLength={200}
                disabled={stopSearchBusy || stopPreviewBusy || liveNavigation.rerouting}
                className="min-h-11 min-w-0 flex-1 rounded-lg border border-blue-700 bg-slate-900 px-3 text-sm text-white placeholder:text-slate-400"
              />
              <button
                type="submit"
                data-testid="run-day-stop-search-submit"
                disabled={!stopSearch.trim() || stopSearchBusy || stopPreviewBusy || liveNavigation.rerouting}
                className="min-h-11 rounded-lg bg-blue-500 px-4 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {stopSearchBusy ? "Searching…" : "Search"}
              </button>
            </div>
          </form>
          <button
            type="button"
            data-testid="run-day-stop-voice-search"
            aria-label={stopVoiceListening ? "Stop destination voice search" : "Say a destination"}
            aria-pressed={stopVoiceListening}
            onClick={startDestinationVoiceSearch}
            disabled={stopSearchBusy || stopPreviewBusy || liveNavigation.rerouting}
            className="mt-2 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-white/30 bg-slate-900/90 px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Mic className="h-4 w-4" />
            {stopVoiceListening ? "Listening for destination…" : "Say a destination"}
          </button>

          {stopPlaces.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="run-day-stop-results" role="list">
              {stopPlaces.map((place) => (
                <button
                  key={place.place_id}
                  type="button"
                  role="listitem"
                  data-testid={`run-day-stop-result-${place.place_id}`}
                  onClick={() => previewStopPlace(place)}
                  disabled={stopSearchBusy || stopPreviewBusy || liveNavigation.rerouting}
                  className="block min-h-12 w-full rounded-lg border border-white/15 bg-slate-900/90 px-3 py-2 text-left hover:bg-slate-800 disabled:opacity-60"
                >
                  <span className="block text-sm font-bold">{place.name}</span>
                  <span className="block text-xs text-slate-300">{place.formatted_address}</span>
                </button>
              ))}
            </div>
          )}

          {stopPreviewBusy && (
            <p data-testid="run-day-stop-preview-loading" role="status" className="mt-2 text-sm text-blue-100">
              Checking the route and booked return time…
            </p>
          )}
          {stopPreview && selectedStop && (
            <div
              data-testid="run-day-stop-preview"
              data-status={stopPreview.status}
              className={`mt-2 rounded-lg border px-3 py-2 text-sm ${stopPreview.status === "OK" ? "border-emerald-500/60 bg-emerald-950/70" : "border-amber-500/60 bg-amber-950/70"}`}
            >
              <p className="font-bold">{selectedStop.name}</p>
              <p>{selectedStop.formatted_address}</p>
              <p className="mt-1">
                Estimated return: {stopPreview.projected_return_at
                  ? new Date(stopPreview.projected_return_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                  : "unavailable"}
                {" · "}Booked deadline: {stopPreview.return_deadline
                  ? new Date(stopPreview.return_deadline).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                  : "unavailable"}
              </p>
              <p className="mt-1">{stopPreview.message}</p>
              {stopPreview.return_deadline_met === false && (
                <p className="mt-1 font-semibold">
                  Go can request the safe partial route; guidance will turn back at its approved point if the full destination does not fit.
                </p>
              )}
              <button
                type="button"
                data-testid="run-day-stop-go"
                onClick={goToSelectedStop}
                disabled={liveNavigation.rerouting || stopPreview.status === "ROUTE_NOT_PROVABLE"}
                className="mt-2 min-h-11 w-full rounded-lg bg-emerald-600 px-4 font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {liveNavigation.rerouting ? "Updating route…" : "Go"}
              </button>
            </div>
          )}

          {stopActionError && (
            <p data-testid="run-day-stop-error" role="status" className="mt-2 text-sm font-semibold text-amber-100">
              {String(stopActionError)}
            </p>
          )}
          <p translate="no" data-testid="google-places-attribution" className="mt-2 text-[11px] text-blue-100">Google Maps</p>
          {liveNavigation.routeNotice && (
            <p data-testid="run-day-stop-notice" role="status" className="mt-2 text-sm font-semibold">
              {liveNavigation.routeNotice}
            </p>
          )}
        </section>
      )}

      {driving && routePoints.length < 2 && !routeStatusOwnedByParent && (
        <div data-testid="driving-route-status" role="status"
          className="pointer-events-none absolute inset-x-4 top-[42%] z-10 rounded-xl bg-slate-950/80 px-4 py-3 text-center text-sm text-amber-100">
          {isPickupJourney
            ? (liveNavigation.error || liveNavigation.routeNotice
              || (liveNavigation.requesting ? "Finding your location for the pupil pickup…" : "Building the route to the pupil pickup…"))
            : (routePending ? "Building the teaching route…" : (routeMessage || "Teaching route unavailable. Map guidance is not ready."))}
          {isPickupJourney && liveNavigation.error && (
            <button type="button" data-testid="retry-pickup-route-btn"
              onClick={liveNavigation.retryRoute}
              disabled={liveNavigation.requesting || liveNavigation.rerouting}
              className="pointer-events-auto mt-3 min-h-11 rounded-lg border border-amber-200/50 px-4 font-semibold disabled:opacity-50">
              Retry pickup route
            </button>
          )}
        </div>
      )}

      {/* Map or empty state */}
      <div className={driving ? "absolute inset-0" : "relative"}>
        {loading && (
          <div data-testid="contextual-map-loading" className={`${driving ? "h-full" : "h-72 sm:h-80"} flex items-center justify-center text-sm text-slate-500`}>
            Loading map…
          </div>
        )}
        {!loading && error && (
          <div data-testid="contextual-map-error" className={`${driving ? "h-full" : "h-72 sm:h-80"} flex flex-col items-center justify-center gap-1 text-sm text-rose-600 px-6 text-center`}>
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        {!loading && !error && !hasAnyPoint && (
          <div data-testid="contextual-map-empty" className={`${driving ? "h-full" : "h-72 sm:h-80"} flex flex-col items-center justify-center gap-1 text-sm text-slate-500 px-6 text-center`}>
            <Info className="w-5 h-5" />
            {!driving && <p>{focus?.lesson_id
              ? "I need the teaching level and pickup address to build this lesson."
              : "No booking to show on the map yet."}</p>}
            {focus?.lesson_id && typeof onNeedsSetup === "function" && (
              <button
                type="button"
                data-testid="contextual-map-complete-setup"
                onClick={onNeedsSetup}
                className="mt-3 min-h-11 rounded-lg bg-blue-700 text-white px-4 font-black"
              >
                Answer the questions
              </button>
            )}
          </div>
        )}
        {!loading && !error && hasAnyPoint && liveNavigation.nativeMode && driving && (
          <NativeNavigationSurface lessonEventId={lessonEventId} />
        )}
        {!loading && !error && hasAnyPoint && !(liveNavigation.nativeMode && driving) && (
          // Leaflet controls restore pointer-events:auto. Native inert locks the
          // whole canvas, including attribution links, without hiding credits
          // or blocking the separate lesson/navigation controls above it.
          <div inert={driving} className={driving ? "h-full" : undefined}>
          <MapContainer
            data-testid="contextual-map-canvas"
            center={DEFAULT_CENTRE}
            zoom={DEFAULT_ZOOM}
            scrollWheelZoom={!driving}
            dragging={!driving}
            touchZoom={!driving}
            doubleClickZoom={!driving}
            boxZoom={!driving}
            keyboard={!driving}
            zoomControl={!driving}
            className={driving ? "h-full w-full z-0" : "h-72 sm:h-80 w-full z-0"}
            style={driving ? { pointerEvents: "none", touchAction: "none" } : undefined}
          >
            <TileLayer
              key={mapTileUrl}
              url={mapTileUrl}
              attribution={mapAttribution}
            />
            {liveNavigation.active ? (
              <FollowLiveDevice device={devCtx} active />
            ) : (
              <FitToContext
                surface={surface}
                driving={driving}
                pickup={pickup}
                dropoff={dropoff}
                device={devCtx}
                // Keep route to pickup out of the active teaching map.
                routePoints={fittedRoutePoints}
                readinessRoutePoints={readinessRoutePoints}
              />
            )}
            {routePoints.length > 1 && (
              <Polyline
                positions={routePoints}
                pathOptions={{ color: "#2563eb", weight: 6, opacity: 0.9 }}
                data-testid="contextual-map-route"
              />
            )}
            {!(surface === "work_mode" && driving) && readinessRoutePoints.length > 1 && (
              <Polyline
                positions={readinessRoutePoints}
                pathOptions={{
                  color: readiness?.status === "TOO_FAR" ? "#e11d48" : "#059669",
                  weight: 5,
                  opacity: 0.9,
                  dashArray: "10 7",
                }}
                data-testid="contextual-map-readiness-route"
              />
            )}
            <BoundsBridge onBounds={fetchEnvironments} />
            <CaptureTapBridge enabled={!driving && captureMode} onPick={setCaptureDraft} />
            <MoveTapBridge enabled={!driving && moveMode} onPick={setMoveDraft} />
            {automaticTeachingPins.map((pin) => {
              const level = Number(pin.level || preferredTeachingLevel) || null;
              const col = LEVEL_COLOUR[level] || LEVEL_COLOUR_UNKNOWN;
              return (
                <CircleMarker
                  key={`automatic-${pin.environment_id || pin.id || pin.position}`}
                  center={[Number(pin.latitude), Number(pin.longitude)]}
                  radius={11}
                  pathOptions={{
                    color: col.stroke, fillColor: col.fill,
                    fillOpacity: 0.95, weight: 4,
                  }}
                  data-testid={`automatic-teaching-pin-${pin.environment_id || pin.id || pin.position}`}
                >
                  <Tooltip permanent direction="top" offset={[0, -7]}>
                    {pin.position || "•"}{level ? ` · L${level}` : ""} {pin.category_label || pin.category || "Practice"}
                  </Tooltip>
                  <Popup>
                    <div className="text-xs">
                      <p className="font-semibold text-slate-900 mb-0.5">{pin.name || "Teaching challenge"}</p>
                      <p className="text-slate-600">NDG automatic teaching pin</p>
                      {(pin.evidence_reason || pin.classification_rule) && (
                        <p className="mt-1 text-slate-500">{pin.evidence_reason || pin.classification_rule}</p>
                      )}
                      <p className="mt-1 text-slate-500">Instructor correction is optional.</p>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
            {/* Batch S3 · draft marker while capturing */}
            {captureMode && captureDraft && (
              <CircleMarker
                center={[captureDraft.latitude, captureDraft.longitude]}
                radius={11}
                pathOptions={{
                  color: "#111827", fillColor: "#111827",
                  fillOpacity: 0.4, weight: 3, dashArray: "4 3",
                }}
                data-testid="env-capture-draft-marker"
              >
                <Tooltip permanent direction="top" offset={[0, -6]}>New</Tooltip>
              </CircleMarker>
            )}
            {/* Batch S5 · preview marker while moving an existing place */}
            {moveMode && moveDraft && (
              <CircleMarker
                center={[moveDraft.latitude, moveDraft.longitude]}
                radius={11}
                pathOptions={{
                  color: "#1d4ed8", fillColor: "#3b82f6",
                  fillOpacity: 0.45, weight: 3, dashArray: "4 3",
                }}
                data-testid="env-move-preview-marker"
              >
                <Tooltip permanent direction="top" offset={[0, -6]}>Moved here</Tooltip>
              </CircleMarker>
            )}
            {/* Educational environment overlay · Batch S2 */}
            {showOverlay && environments.map((env) => {
              const col = LEVEL_COLOUR[env.level] || LEVEL_COLOUR_UNKNOWN;
              return (
                <CircleMarker
                  key={env.id}
                  center={[env.latitude, env.longitude]}
                  radius={9}
                  pathOptions={{
                    color: col.stroke, fillColor: col.fill,
                    fillOpacity: 0.85, weight: 2,
                  }}
                  data-testid={`env-marker-${env.id}`}
                >
                  <Tooltip direction="top" offset={[0, -6]}>
                    {env.category_label}{env.level ? ` · L${env.level}` : ""}
                  </Tooltip>
                  <Popup>
                    <div className="text-xs" data-testid={`env-popup-${env.id}`}>
                      <p className="font-semibold text-slate-900 mb-0.5">{env.title || env.category_label}</p>
                      <p className="text-slate-500 mb-1">
                        {env.category_label}{env.level ? ` · Level ${env.level}` : ""}
                      </p>
                      <button
                        type="button"
                        data-testid={`env-open-detail-${env.id}`}
                        onClick={() => openDetail(env.id)}
                        className="mt-1 rounded-md bg-slate-900 text-white px-2 py-1 text-[11px] font-semibold"
                      >
                        Open details
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
            {pickup?.latitude != null && (
              <CircleMarker
                center={[pickup.latitude, pickup.longitude]}
                radius={12}
                pathOptions={{ color: "#0369a1", fillColor: "#0ea5e9", fillOpacity: 0.85, weight: 3 }}
                data-testid="contextual-map-pickup-marker"
              >
                <Tooltip permanent direction="top" offset={[0, -6]}>Pickup</Tooltip>
              </CircleMarker>
            )}
            {dropoff?.latitude != null && (
              <CircleMarker
                center={[dropoff.latitude, dropoff.longitude]}
                radius={12}
                pathOptions={{ color: "#7c2d12", fillColor: "#f97316", fillOpacity: 0.85, weight: 3 }}
                data-testid="contextual-map-dropoff-marker"
              >
                <Tooltip permanent direction="top" offset={[0, -6]}>Return</Tooltip>
              </CircleMarker>
            )}
            {devCtx?.latitude != null && (
              <CircleMarker
                center={[devCtx.latitude, devCtx.longitude]}
                radius={10}
                pathOptions={{ color: "#065f46", fillColor: "#10b981", fillOpacity: 0.9, weight: 3 }}
                data-testid="contextual-map-device-marker"
              >
                <Tooltip permanent direction="top" offset={[0, -6]}>You</Tooltip>
              </CircleMarker>
            )}
          </MapContainer>
          </div>
        )}
      </div>

      {!driving && <>
      {/* Status footer — big touch targets, minimal density */}
      <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-2">
        {pickup && (
          <StatusPill status={pickup.status} testid="contextual-map-pickup-status">
            Pickup: {STATUS_LABEL[pickup.status] || pickup.status}
          </StatusPill>
        )}
        {dropoff && (
          <StatusPill status={dropoff.status} testid="contextual-map-dropoff-status">
            Return: {STATUS_LABEL[dropoff.status] || dropoff.status}
          </StatusPill>
        )}
        {devCtx && (
          <StatusPill status={devCtx.status} testid="contextual-map-device-status">
            You: {STATUS_LABEL[devCtx.status] || devCtx.status}
          </StatusPill>
        )}
        {routePoints.length > 1 && (
          <StatusPill status="confirmed" testid="contextual-map-route-status">
            Google route
          </StatusPill>
        )}
        {liveMode && automaticTeachingPins.length > 0 && (
          <StatusPill status="confirmed" testid="contextual-map-challenge-count">
            {automaticTeachingPins.length} route {automaticTeachingPins.length === 1 ? "challenge" : "challenges"}
          </StatusPill>
        )}
        <div className="ml-auto flex items-center gap-2">
          {liveMode && (
            <button
              type="button"
              data-testid="repeat-last-challenge-btn"
              onClick={liveNavigation.repeatLastChallenge}
              disabled={!liveNavigation.canRepeat}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-blue-500 bg-blue-950 px-3 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Repeat the last driving challenge"
            >
              <RotateCcw className="w-4 h-4" />
              Repeat
            </button>
          )}
          <button
            type="button"
            data-testid="contextual-map-locate-btn"
            onClick={handleGo}
            disabled={goDisabled}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 disabled:opacity-50"
          >
            <Navigation className="w-3.5 h-3.5" />
            {deviceLabel}
          </button>
        </div>
      </div>

      {/* Educational Environment Overlay · Batch S2 (UX revision)
       *
       * Two-tier control — the trigger sits on a single line so the
       * map stays the primary working surface. The filter panel is
       * DOCKED (reserves its own space when open), never a floating
       * layer over the map. Auto-closes when the user leaves the
       * strip idle. */}
      <div
        data-testid="environment-overlay-strip"
        className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40"
      >
        <div className="px-3 py-2 flex items-center gap-2">
          <button
            type="button"
            data-testid="environment-overlay-toggle"
            onClick={() => {
              if (!showOverlay) {
                setShowOverlay(true);
                setFiltersOpen(false);
              } else if (!filtersOpen) {
                setFiltersOpen(true);
              } else {
                setShowOverlay(false);
                setFiltersOpen(false);
              }
            }}
            aria-expanded={filtersOpen}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border ${
              showOverlay
                ? "border-slate-900 dark:border-white bg-slate-900 dark:bg-white text-white dark:text-slate-900"
                : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            Teaching places
            <span
              data-testid="environment-overlay-count"
              className="ml-1 rounded-full bg-black/10 dark:bg-black/20 px-1.5 text-[10px] font-bold"
            >
              {showOverlay ? envMeta.count : "–"}
            </span>
          </button>
          {/* Batch S3 · Capture trigger — docked next to the overlay
             *  toggle so it never floats over the map. */}
          {allowManualCapture && (!captureMode ? (
            <button
              type="button"
              data-testid="env-capture-start"
              onClick={startCapture}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200"
            >
              <Plus className="w-3.5 h-3.5" />
              Capture Teaching Place
            </button>
          ) : (
            <span
              data-testid="env-capture-hint"
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-400 dark:border-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200"
            >
              <Target className="w-3.5 h-3.5" />
              Tap the map…
            </span>
          ))}
          {envError && (
            <span data-testid="environment-overlay-error" className="text-[11px] text-rose-600 truncate">{envError}</span>
          )}
        </div>

        {showOverlay && filtersOpen && (
          <div
            data-testid="environment-overlay-panel"
            className="px-3 pb-3 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2"
          >            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="env-level-legend">
                <span>Level:</span>
                {[1, 2, 3, 4].map((lvl) => (
                  <span
                    key={lvl}
                    data-testid={`env-legend-${lvl}`}
                    className="inline-flex items-center gap-1"
                  >
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ background: LEVEL_COLOUR[lvl].fill, borderColor: LEVEL_COLOUR[lvl].stroke }}
                    />
                    {lvl}
                  </span>
                ))}
              </div>
              <button
                type="button"
                data-testid="environment-overlay-close"
                onClick={() => setFiltersOpen(false)}
                aria-label="Close teaching places filters"
                className="inline-flex items-center rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1" data-testid="env-category-chips">
              {CATEGORY_KEYS.map((c) => {
                const on = enabledCats.has(c);
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`env-cat-${c}`}
                    onClick={() => {
                      toggleCat(c);
                      // Auto-close after a category tap so the panel
                      // never lingers over the working surface. The
                      // instructor can re-open it any time.
                      setTimeout(() => setFiltersOpen(false), 180);
                    }}
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium border ${
                      on
                        ? "border-slate-900 dark:border-white bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        : "border-slate-200 dark:border-slate-700 bg-transparent text-slate-400"
                    }`}
                  >
                    {c.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Batch S3 · Capture Teaching Place sheet — docked below the
       *  overlay strip. Reserves layout space, NEVER floats over
       *  the map, markers or bottom nav. */}
      {captureMode && captureDraft && (
        <div
          data-testid="env-capture-sheet"
          className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-3 space-y-2"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              New teaching place
            </p>
            <p className="text-[11px] text-slate-400 tabular-nums">
              {captureDraft.latitude.toFixed(5)}, {captureDraft.longitude.toFixed(5)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-slate-500 space-y-1">
              Category
              <select
                data-testid="env-capture-category"
                value={captureForm.category}
                onChange={(e) => setCaptureForm((f) => ({ ...f, category: e.target.value }))}
                className="mt-0.5 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
              >
                {CATEGORY_KEYS.map((c) => (
                  <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                ))}
              </select>
            </label>
            <label className="text-[11px] text-slate-500 space-y-1">
              Level (1-4)
              <select
                data-testid="env-capture-level"
                value={captureForm.level}
                onChange={(e) => setCaptureForm((f) => ({ ...f, level: Number(e.target.value) }))}
                className="mt-0.5 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
              >
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>Level {n}</option>)}
              </select>
            </label>
          </div>
          <input
            data-testid="env-capture-title"
            value={captureForm.title}
            onChange={(e) => setCaptureForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Title (optional)"
            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
            maxLength={120}
          />
          <textarea
            data-testid="env-capture-description"
            value={captureForm.description}
            onChange={(e) => setCaptureForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Description (optional)"
            rows={2}
            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
            maxLength={240}
          />
          <input
            data-testid="env-capture-hazards"
            value={captureForm.hazards}
            onChange={(e) => setCaptureForm((f) => ({ ...f, hazards: e.target.value }))}
            placeholder="Hazards (comma-separated, optional)"
            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
          />
          <input
            data-testid="env-capture-prereq"
            value={captureForm.prerequisite_skills}
            onChange={(e) => setCaptureForm((f) => ({ ...f, prerequisite_skills: e.target.value }))}
            placeholder="Prerequisite skills (comma-separated, optional)"
            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
          />
          <input
            data-testid="env-capture-confidence"
            value={captureForm.confidence_expectation}
            onChange={(e) => setCaptureForm((f) => ({ ...f, confidence_expectation: e.target.value }))}
            placeholder="Confidence expectation (optional)"
            className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
            maxLength={240}
          />
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              data-testid="env-capture-save"
              onClick={submitCapture}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-3 py-2 text-xs font-semibold disabled:bg-slate-300"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              data-testid="env-capture-cancel"
              onClick={cancelCapture}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Batch S4 · Environment Detail Sheet — docked below the map,
       *  never a floating modal. Reserves layout space. */}
      {detail && (
        <div
          data-testid="env-detail-sheet"
          className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-3 space-y-2"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Teaching place
              </p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white" data-testid="env-detail-title">
                {detail.title || detail.category_label}
              </p>
              <p className="text-[11px] text-slate-500">
                {detail.category_label}{detail.level ? ` · Level ${detail.level}` : ""} · <span className="uppercase tracking-wide" data-testid="env-detail-source">{(detail.source || "").replace(/_/g, " ")}</span>{detail.status !== "active" ? <> · <span data-testid="env-detail-status" className="text-amber-700">{detail.status}</span></> : null}
              </p>
            </div>
            <button
              type="button"
              data-testid="env-detail-close"
              onClick={closeDetail}
              aria-label="Close detail sheet"
              className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {!detailEditing ? (
            <>
              {detail.description && (
                <p className="text-xs text-slate-700 dark:text-slate-300" data-testid="env-detail-description">{detail.description}</p>
              )}
              {detail.hazards?.length > 0 && (
                <p className="text-xs text-slate-600" data-testid="env-detail-hazards">
                  <span className="font-semibold">Hazards:</span> {detail.hazards.join(", ")}
                </p>
              )}
              {detail.prerequisite_skills?.length > 0 && (
                <p className="text-xs text-slate-600" data-testid="env-detail-prereq">
                  <span className="font-semibold">Prerequisites:</span> {detail.prerequisite_skills.join(", ")}
                </p>
              )}
              {detail.confidence_expectation && (
                <p className="text-xs text-slate-600" data-testid="env-detail-confidence">
                  <span className="font-semibold">Confidence:</span> {detail.confidence_expectation}
                </p>
              )}
              <div className="pt-1 text-[11px] text-slate-400">
                {detail.created_by && (<span data-testid="env-detail-created-by">Created by <span className="font-mono">{String(detail.created_by).slice(0, 8)}…</span></span>)}
                {detail.created_at && (<span data-testid="env-detail-created-at" className="ml-2">Added {new Date(detail.created_at).toLocaleDateString()}</span>)}
                {detail.updated_at && detail.updated_at !== detail.created_at && (
                  <span data-testid="env-detail-updated-at" className="ml-2">· Updated {new Date(detail.updated_at).toLocaleDateString()}</span>
                )}
              </div>
              {detail.source === "instructor_confirmed" && detail.status !== "retired" && (
                !moveMode && !reflectMode ? (
                  <div className="pt-2 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid="env-detail-edit"
                      onClick={startEdit}
                      disabled={detailBusy}
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-3 py-2 text-xs font-semibold disabled:bg-slate-300"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      data-testid="env-detail-move-start"
                      onClick={startMove}
                      disabled={detailBusy}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200"
                    >
                      <Target className="w-3.5 h-3.5" />
                      Move place
                    </button>
                    <button
                      type="button"
                      data-testid="env-detail-reflect-start"
                      onClick={startReflect}
                      disabled={detailBusy}
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 text-emerald-700 dark:text-emerald-300 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold"
                    >
                      <Info className="w-3.5 h-3.5" />
                      Add reflection
                    </button>
                    <button
                      type="button"
                      data-testid="env-detail-retire"
                      onClick={retireDetail}
                      disabled={detailBusy}
                      className="inline-flex items-center gap-1 rounded-lg border border-rose-300 text-rose-700 px-3 py-2 text-xs font-semibold"
                    >
                      Retire
                    </button>
                  </div>
                ) : moveMode ? (
                  <div
                    data-testid="env-detail-move-bar"
                    className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Move teaching place
                      </p>
                      <p className="text-[11px] tabular-nums text-slate-500" data-testid="env-detail-move-preview">
                        {moveDraft
                          ? `${moveDraft.latitude.toFixed(5)}, ${moveDraft.longitude.toFixed(5)}`
                          : "Tap the map…"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="env-detail-move-confirm"
                        onClick={confirmMove}
                        disabled={!moveDraft || moveBusy}
                        className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-3 py-2 text-xs font-semibold disabled:bg-slate-300"
                      >
                        {moveBusy ? "Moving…" : "Confirm move"}
                      </button>
                      <button
                        type="button"
                        data-testid="env-detail-move-cancel"
                        onClick={cancelMove}
                        disabled={moveBusy}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    data-testid="env-detail-reflect-sheet"
                    className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/20 px-3 py-3 space-y-2"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                      Post-lesson reflection
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        data-testid="env-detail-reflect-confidence"
                        value={reflectForm.confidence}
                        onChange={(e) => setReflectForm((f) => ({ ...f, confidence: e.target.value }))}
                        placeholder="Confidence (e.g. confident, shaky)"
                        maxLength={64}
                        className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                      />
                      <input
                        data-testid="env-detail-reflect-outcome"
                        value={reflectForm.outcome}
                        onChange={(e) => setReflectForm((f) => ({ ...f, outcome: e.target.value }))}
                        placeholder="Outcome (e.g. completed, partial)"
                        maxLength={64}
                        className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        data-testid="env-detail-reflect-lesson-id"
                        value={reflectForm.lesson_id}
                        onChange={(e) => setReflectForm((f) => ({ ...f, lesson_id: e.target.value }))}
                        placeholder="Lesson id (optional)"
                        maxLength={64}
                        className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                      />
                      <input
                        data-testid="env-detail-reflect-learner-id"
                        value={reflectForm.learner_id}
                        onChange={(e) => setReflectForm((f) => ({ ...f, learner_id: e.target.value }))}
                        placeholder="Learner id (optional)"
                        maxLength={64}
                        className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <textarea
                      data-testid="env-detail-reflect-notes"
                      value={reflectForm.notes}
                      onChange={(e) => setReflectForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="Notes (what happened, observations)"
                      rows={2}
                      maxLength={1000}
                      className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                    <input
                      data-testid="env-detail-reflect-hazards"
                      value={reflectForm.hazards_observed}
                      onChange={(e) => setReflectForm((f) => ({ ...f, hazards_observed: e.target.value }))}
                      placeholder="Hazards observed (comma-separated)"
                      className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                    <textarea
                      data-testid="env-detail-reflect-worked-well"
                      value={reflectForm.worked_well}
                      onChange={(e) => setReflectForm((f) => ({ ...f, worked_well: e.target.value }))}
                      placeholder="Worked well"
                      rows={2}
                      maxLength={500}
                      className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                    <textarea
                      data-testid="env-detail-reflect-needs-improvement"
                      value={reflectForm.needs_improvement}
                      onChange={(e) => setReflectForm((f) => ({ ...f, needs_improvement: e.target.value }))}
                      placeholder="Needs improvement"
                      rows={2}
                      maxLength={500}
                      className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
                    />
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        data-testid="env-detail-reflect-save"
                        onClick={submitReflection}
                        disabled={reflectBusy}
                        className="inline-flex items-center gap-1 rounded-lg bg-emerald-700 text-white px-3 py-2 text-xs font-semibold disabled:bg-slate-300"
                      >
                        {reflectBusy ? "Saving…" : "Save reflection"}
                      </button>
                      <button
                        type="button"
                        data-testid="env-detail-reflect-cancel"
                        onClick={cancelReflect}
                        disabled={reflectBusy}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              )}
              {/* Batch S7 · Reflection Timeline · docked history section.
               *  Renders only for owned+active rows (matches the backend
               *  404 discipline). No modal, no floating layer.
               */}
              {detail.source === "instructor_confirmed" && detail.status !== "retired" && (
                <div
                  data-testid="env-detail-reflect-history"
                  className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Reflection history
                    </p>
                    <span
                      data-testid="env-detail-reflect-history-count"
                      className="rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 text-[10px] font-bold text-slate-600 dark:text-slate-300"
                    >
                      {reflectionHistory.length}
                    </span>
                  </div>
                  {historyLoading ? (
                    <p data-testid="env-detail-reflect-history-loading" className="text-[11px] text-slate-500">Loading…</p>
                  ) : historyError ? (
                    <p data-testid="env-detail-reflect-history-error" className="text-[11px] text-rose-600">{historyError}</p>
                  ) : reflectionHistory.length === 0 ? (
                    <p data-testid="env-detail-reflect-history-empty" className="text-[11px] text-slate-500 italic">
                      No reflections recorded here yet.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {reflectionHistory.map((r) => (
                        <li
                          key={r.id}
                          data-testid={`env-detail-reflect-history-item-${r.id}`}
                          className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-2 py-1.5 space-y-1"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[11px] tabular-nums text-slate-500" data-testid={`env-detail-reflect-history-when-${r.id}`}>
                              {r.created_at ? new Date(r.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—"}
                            </span>
                            <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500">
                              {r.confidence && (
                                <span data-testid={`env-detail-reflect-history-confidence-${r.id}`} className="rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 px-1 py-0.5">
                                  {r.confidence}
                                </span>
                              )}
                              {r.outcome && (
                                <span data-testid={`env-detail-reflect-history-outcome-${r.id}`} className="rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-1 py-0.5">
                                  {r.outcome}
                                </span>
                              )}
                            </span>
                          </div>
                          {r.notes && (
                            <p className="text-[11px] text-slate-700 dark:text-slate-300" data-testid={`env-detail-reflect-history-notes-${r.id}`}>
                              {r.notes}
                            </p>
                          )}
                          {r.hazards_observed?.length > 0 && (
                            <p className="text-[11px] text-slate-600" data-testid={`env-detail-reflect-history-hazards-${r.id}`}>
                              <span className="font-semibold">Hazards:</span> {r.hazards_observed.join(", ")}
                            </p>
                          )}
                          {r.worked_well && (
                            <p className="text-[11px] text-slate-600" data-testid={`env-detail-reflect-history-worked-${r.id}`}>
                              <span className="font-semibold">Worked well:</span> {r.worked_well}
                            </p>
                          )}
                          {r.needs_improvement && (
                            <p className="text-[11px] text-slate-600" data-testid={`env-detail-reflect-history-needs-${r.id}`}>
                              <span className="font-semibold">Needs improvement:</span> {r.needs_improvement}
                            </p>
                          )}
                          {(r.lesson_id || r.learner_id) && (
                            <p className="text-[10px] text-slate-400" data-testid={`env-detail-reflect-history-refs-${r.id}`}>
                              {r.lesson_id && <>Lesson <span className="font-mono">{r.lesson_id}</span></>}
                              {r.lesson_id && r.learner_id && <> · </>}
                              {r.learner_id && <>Learner <span className="font-mono">{r.learner_id}</span></>}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {detail.source !== "instructor_confirmed" && (
                <p className="pt-2 text-[11px] text-slate-400 italic" data-testid="env-detail-readonly">
                  Founder-seeded — read only
                </p>
              )}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[11px] text-slate-500 space-y-1">
                  Category
                  <select
                    data-testid="env-detail-edit-category"
                    value={detailForm.category}
                    onChange={(e) => setDetailForm((f) => ({ ...f, category: e.target.value }))}
                    className="mt-0.5 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
                  >
                    {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-slate-500 space-y-1">
                  Level (1-4)
                  <select
                    data-testid="env-detail-edit-level"
                    value={detailForm.level}
                    onChange={(e) => setDetailForm((f) => ({ ...f, level: Number(e.target.value) }))}
                    className="mt-0.5 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
                  >
                    {[1, 2, 3, 4].map((n) => <option key={n} value={n}>Level {n}</option>)}
                  </select>
                </label>
              </div>
              <input
                data-testid="env-detail-edit-title"
                value={detailForm.title}
                onChange={(e) => setDetailForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Title"
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
                maxLength={120}
              />
              <textarea
                data-testid="env-detail-edit-description"
                value={detailForm.description}
                onChange={(e) => setDetailForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Description"
                rows={2}
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
                maxLength={240}
              />
              <input
                data-testid="env-detail-edit-hazards"
                value={detailForm.hazards}
                onChange={(e) => setDetailForm((f) => ({ ...f, hazards: e.target.value }))}
                placeholder="Hazards (comma-separated)"
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
              />
              <input
                data-testid="env-detail-edit-prereq"
                value={detailForm.prerequisite_skills}
                onChange={(e) => setDetailForm((f) => ({ ...f, prerequisite_skills: e.target.value }))}
                placeholder="Prerequisite skills (comma-separated)"
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
              />
              <input
                data-testid="env-detail-edit-confidence"
                value={detailForm.confidence_expectation}
                onChange={(e) => setDetailForm((f) => ({ ...f, confidence_expectation: e.target.value }))}
                placeholder="Confidence expectation"
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
                maxLength={240}
              />
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  data-testid="env-detail-edit-save"
                  onClick={saveEdit}
                  disabled={detailBusy}
                  className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-3 py-2 text-xs font-semibold disabled:bg-slate-300"
                >
                  {detailBusy ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  data-testid="env-detail-edit-cancel"
                  onClick={() => { setDetailEditing(false); setDetailForm(null); }}
                  disabled={detailBusy}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-semibold"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
      </>}
    </section>
  );
}

